import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AuthSignUpAttributesEnum } from '../../enums/auth/auth-sign-up-attributes.enum';
import { BooleanMessage } from '../../messages/boolean.message';
import { BrokerModel } from '../../models/auth/broker.model';
import { ConfirmRegistrationModel } from '../../models/auth/confirm-registration.model';
import { EmptyMessage } from '../../messages/empty.message';
import { environment } from '../../../../environments/environment';
import { ErrorResponseMessage } from '../../messages/error-response.message';
import { FcaNumberModel } from '../../models/auth/fca-number.model';
import { FcaNumberValidationModel } from '../../models/auth/fca-number-validation.model';
import { LoggerService } from '../logger/logger.service';
import { LoginModel } from '../../models/auth/login.model';
import { MixpanelService } from '../mixpanel/mixpanel.service';
import { RecoverPasswordModel } from '../../models/auth/recover-password.model';
import { RegisterModel } from '../../models/auth/register.model';
import { ResetPasswordModel } from '../../models/auth/reset-password.model';
import { StorageKeyEnum } from '../storage/enum/storage-key.enum';
import { StorageService } from '../storage/storage.service';
import { UserEmailValidationModel } from '../../models/auth/user-email-validation.model';
import { UserService } from '../user/user.service';
import { UserValidationModel } from '../user/model/user-validation.model';
import { FeatureFlagsService } from '../feature-flags/feature-flags.service';

/**
 * Service to manage AWS Cognito authentication.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * If the user can submit the consumer flow.
   */
  get canSubmitConsumerFlow(): boolean {
    return this.storage.getValue(StorageKeyEnum.CAN_SUBMIT_CONSUMER_FLOW) !== null;
  }

  set canSubmitConsumerFlow(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.CAN_SUBMIT_CONSUMER_FLOW, value ? 'true' : null);
  }

  /**
   * If the user can request admin access.
   */
  get canRequestAdminAccess(): boolean {
    return this.storage.getValue(StorageKeyEnum.CAN_REQUEST_ADMIN_ACCESS) !== null;
  }

  set canRequestAdminAccess(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.CAN_REQUEST_ADMIN_ACCESS, value ? 'true' : null);
    if (!value) {
      this.isAdminViewEnabled = false;
    }
  }

  /**
   * If the user can request any broker account access.
   */
  get canRequestAnyAccountAccess(): boolean {
    return this.storage.getValue(StorageKeyEnum.CAN_REQUEST_ANY_ACCOUNT_ACCESS) !== null;
  }

  set canRequestAnyAccountAccess(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.CAN_REQUEST_ANY_ACCOUNT_ACCESS, value ? 'true' : null);
    if (!value) {
      this.isAdminViewEnabled = false;
    }
  }

  /**
   * If the admin view is enabled.
   */
  get isAdminViewEnabled(): boolean {
    return this.storage.getValue(StorageKeyEnum.IS_ADMIN_VIEW_ENABLED) !== null;
  }

  set isAdminViewEnabled(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.IS_ADMIN_VIEW_ENABLED, value ? 'true' : null);
  }

  /**
   * If any broker account view is enabled.
   */
  get isAnyAccountViewEnabled(): boolean {
    return this.storage.getValue(StorageKeyEnum.IS_ANY_ACCOUNT_VIEW_ENABLED) !== null;
  }

  set isAnyAccountViewEnabled(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.IS_ANY_ACCOUNT_VIEW_ENABLED, value ? 'true' : null);
  }

  get isUserActive(): boolean {
    return this.storage.getValue(StorageKeyEnum.IS_USER_ACTIVE) === 'true';
  }

  set isUserActive(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.IS_USER_ACTIVE, String(value));
  }

  get isAdvicePlus(): boolean {
    return this.storage.getValue(StorageKeyEnum.IS_ADVICE_PLUS) === 'true';
  }

  set isAdvicePlus(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.IS_ADVICE_PLUS, String(value));
  }

  get brokerData(): BrokerModel {
    const data: string = this.storage.getValue(StorageKeyEnum.BROKER_DATA);
    if (data) {
      return JSON.parse(data);
    }
    return null;
  }

  set brokerData(value: BrokerModel) {
    this.storage.saveValue(StorageKeyEnum.BROKER_DATA, value ? JSON.stringify(value) : null);
  }

  get isWhatsNewAllowed(): boolean {
    return this.storage.getValue(StorageKeyEnum.IS_WHATS_NEW_POPUP_ALLOWED) === 'true';
  }

  set isWhatsNewAllowed(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.IS_WHATS_NEW_POPUP_ALLOWED, String(value));
  }

  get isSelinaEmployee(): boolean {
    return this.storage.getValue(StorageKeyEnum.IS_SELINA_EMPLOYEE) === 'true';
  }

  set isSelinaEmployee(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.IS_SELINA_EMPLOYEE, String(value));
  }

  get isWhatsNewPopupAllowedHomeTrack(): boolean {
    return this.storage.getValue(StorageKeyEnum.IS_WHATS_NEW_POPUP_ALLOWED_HOME_TRACK) === 'true';
  }

  set isWhatsNewPopupAllowedHomeTrack(value: boolean) {
    this.storage.saveValue(StorageKeyEnum.IS_WHATS_NEW_POPUP_ALLOWED_HOME_TRACK, String(value));
  }

  get registeredUser(): string {
    return this.storage.getValue(StorageKeyEnum.REGISTERED_USER_INFO);
  }

  set registeredUser(value: string) {
    this.storage.saveValue(StorageKeyEnum.REGISTERED_USER_INFO, value);
  }

  get fcaNumber(): string {
    return this.storage.getValue(StorageKeyEnum.SESSION_FCA_NUMBER);
  }

  set fcaNumber(value: string) {
    this.storage.saveValue(StorageKeyEnum.SESSION_FCA_NUMBER, value);
  }

  get recoverEmail(): string {
    return this.storage.getValue(StorageKeyEnum.SESSION_RECOVER_EMAIL);
  }

  set recoverEmail(value: string) {
    this.storage.saveValue(StorageKeyEnum.SESSION_RECOVER_EMAIL, value);
  }

  get accessToken(): string {
    return this.storage.getValue(StorageKeyEnum.ACCESS_TOKEN);
  }

  set accessToken(value: string) {
    this.storage.saveValue(StorageKeyEnum.ACCESS_TOKEN, value);
  }

  get idToken(): string {
    return this.storage.getValue(StorageKeyEnum.ID_TOKEN);
  }

  set idToken(value: string) {
    this.storage.saveValue(StorageKeyEnum.ID_TOKEN, value);
  }

  get refreshToken(): string {
    return this.storage.getValue(StorageKeyEnum.REFRESH_TOKEN);
  }

  set refreshToken(value: string) {
    this.storage.saveValue(StorageKeyEnum.REFRESH_TOKEN, value);
  }

  private isLoggedInPromise: Promise<boolean> | null = null;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly logger: LoggerService,
    private readonly userService: UserService,
    private readonly storage: StorageService,
    private readonly featureFlags: FeatureFlagsService
  ) {}

  private get getTokens(): Promise<[string, string]> {
    return new Promise((resolve) => {
      if (!this.accessToken) {
        resolve(null);
        return;
      }

      const tokenData = this.parseJwt(this.accessToken);
      const currentTime = Math.floor(Date.now() / 1000);

      if (!tokenData || tokenData.exp < currentTime) {
        this.httpClient
          .post<any>(`${environment.consumerApiPath}/api/auth/refresh-token`, {
            refreshToken: this.refreshToken
          })
          .subscribe({
            next: async (response) => {
              this.setAuthTokens(response);

              resolve([this.accessToken, this.idToken]);
            },
            error: () => {
              this.logout();
              resolve(null);
            }
          });
      } else {
        resolve([this.accessToken, this.idToken]);
      }
    });
  }

  init(): Promise<void> {
    return new Promise<void>((resolve: EmptyMessage): void => {
      this.isLoggedIn().then((loggedIn: boolean) => {
        if (loggedIn) {
          this.validateUserAccount().then(() => {
            resolve();
          });
        } else {
          this.canSubmitConsumerFlow = false;
          this.canRequestAdminAccess = false;
          this.canRequestAnyAccountAccess = false;
          resolve();
        }
      });
    });
  }

  /**
   * Indicates if the user is already logged in.
   */
  isLoggedIn(): Promise<boolean> {
    if (this.isLoggedInPromise) {
      return this.isLoggedInPromise;
    }

    this.isLoggedInPromise = new Promise((resolve) => {
      if (!this.accessToken) {
        this.isLoggedInPromise = null;
        resolve(false);
        return;
      }

      const tokenData = this.parseJwt(this.accessToken);
      const currentTime = Math.floor(Date.now() / 1000);

      if (tokenData && tokenData.exp > currentTime) {
        this.isLoggedInPromise = null;
        resolve(true);
        return;
      }

      if (!this.refreshToken) {
        this.isLoggedInPromise = null;
        resolve(false);
        return;
      }

      this.httpClient
        .post<any>(`${environment.consumerApiPath}/api/auth/refresh-token`, {
          refreshToken: this.refreshToken
        })
        .subscribe({
          next: async (response) => {
            this.setAuthTokens(response);
            this.isLoggedInPromise = null;
            resolve(true);
          },
          error: () => {
            this.logout();
            this.isLoggedInPromise = null;
            resolve(false);
          }
        });
    });

    return this.isLoggedInPromise;
  }

  /**
   * Gets the access token.
   */
  getAccessToken(): Promise<[string, string]> {
    return this.getTokens;
  }

  async getCurrentUserData(attributes?: any): Promise<RegisterModel> {
    return new Promise((resolve) => {
      if (!attributes) {
        attributes = this.parseJwt(this.idToken);
      }

      const model: Partial<RegisterModel> = {
        email: attributes.email || '',
        firstName: attributes.given_name || '',
        lastName: attributes.family_name || '',
        fcaNumber: attributes['custom:fca_number'] || '',
        phoneNumber: attributes.phone_number || ''
      };

      resolve(model as RegisterModel);
    });
  }

  /**
   * Clean up stored data.
   */
  cleanUp(): void {
    this.registeredUser = null;
    this.fcaNumber = null;
    this.recoverEmail = null;
  }

  parseJwt = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    } catch (error) {
      return null;
    }
  };

  /**
   * Executes login request.
   */
  async login(credentials: LoginModel): Promise<[boolean, RegisterModel]> {
    return new Promise<[boolean, RegisterModel | null]>((resolve): void => {
      this.httpClient
        .post<any>(`${environment.consumerApiPath}/api/auth/token`, {
          username: credentials.email,
          password: credentials.password
        })
        .subscribe({
          next: async (response) => {
            this.setAuthTokens(response);

            try {
              const attributes = this.parseJwt(this.idToken);
              await this.validateUserAccount();
              const brokerageName = this.brokerData?.firmName || '';
              MixpanelService.registerSuperProperties(attributes.email, attributes['custom:fca_number'], brokerageName);
              const model: RegisterModel = await this.getCurrentUserData(attributes);

              this.cleanUp();

              this.isLoggedInPromise = null;

              resolve([true, model]);
            } catch (error) {
              this.logger.log('Error signing in:', error);
              resolve([false, null]);
            }
          },
          error: (error: HttpErrorResponse) => {
            this.logger.log('Error signing in:', error);
            resolve([false, null]);
          }
        });
    });
  }

  /**
   * Executes logout request.
   */
  async logout(): Promise<boolean> {
    return new Promise<boolean>((resolve): void => {
      if (this.accessToken && this.refreshToken) {
        this.httpClient
          .post(`${environment.consumerApiPath}/api/auth/logout`, {
            accessToken: this.accessToken,
            refreshToken: this.refreshToken
          })
          .subscribe({
            next: async () => {
              this.cleanUp();
              this.featureFlags.resetFeatureFlagsEnabled();
              this.storage.clearBrowserStorage();
              this.isLoggedInPromise = null;

              resolve(true);
            },
            error: (error) => {
              this.logger.log('Error signing out: ', error);
              resolve(false);
            }
          });
      }
    });
  }

  /**
   * Validate if an FCA number is registered.
   */
  validateFcaNumber(value: FcaNumberModel): Promise<boolean> {
    return new Promise<boolean>((resolve: BooleanMessage, reject: ErrorResponseMessage): void => {
      this.httpClient
        .post<FcaNumberValidationModel>(`${environment.consumerApiPath}/user/validateFcaNumber`, {
          fcaNumber: value.fcaNumber
        })
        .subscribe(
          (response: FcaNumberValidationModel) => resolve(response.fcaNumberExists),
          (error: HttpErrorResponse) => reject(error)
        );
    });
  }

  /**
   * Sign up a new user.
   */
  async register(value: RegisterModel): Promise<boolean> {
    const exists: boolean = await this.validateUserEmail(value.email);
    if (exists) {
      return false;
    }
    return new Promise<boolean>((resolve): void => {
      this.httpClient
        .post(`${environment.consumerApiPath}/api/user/create`, {
          username: value.email,
          password: value.password,
          userAttributes: [
            { name: AuthSignUpAttributesEnum.GIVEN_NAME, value: value.firstName },
            { name: AuthSignUpAttributesEnum.FAMILY_NAME, value: value.lastName },
            { name: AuthSignUpAttributesEnum.EMAIL, value: value.email },
            { name: AuthSignUpAttributesEnum.PHONE_NUMBER, value: value.phoneNumber },
            { name: AuthSignUpAttributesEnum.FCA_NUMBER, value: value.fcaNumber },
            {
              name: AuthSignUpAttributesEnum.AGREED_TO_TERMS_AND_CONDITIONS,
              value: value.acceptedTermsAndConditions ? 'true' : 'false'
            }
          ]
        })
        .subscribe({
          next: async () => {
            resolve(true);
          },
          error: (error: HttpErrorResponse) => {
            if (error.message === 'UsernameExistsException') {
              resolve(false);
            }
            this.logger.log('Error signing in: ', error);
            throw error;
          }
        });
    });
  }

  /**
   * Confirm sign up of a new user.
   */
  async confirmRegistration(value: ConfirmRegistrationModel): Promise<boolean> {
    return new Promise<boolean>((resolve): void => {
      this.httpClient
        .post(`${environment.consumerApiPath}/api/user/confirm`, {
          username: value.email,
          code: value.code
        })
        .subscribe({
          next: async () => {
            resolve(true);
          },
          error: (error) => {
            this.logger.log('Error confirming registration: ', error);
            resolve(false);
          }
        });
    });
  }

  /**
   * Resend the confirmation code.
   */
  async resendCode(email: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.httpClient
        .post(`${environment.consumerApiPath}/api/user/resend-confirmation-code`, {
          email: email
        })
        .subscribe({
          next: () => {
            resolve(true);
          },
          error: (error) => {
            this.logger.log('Error resending code: ', error);
            resolve(false);
          }
        });
    });
  }

  /**
   * Request a password recovery for a user.
   */
  async requestPasswordRecovery(value: RecoverPasswordModel): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.httpClient
        .post(
          `${environment.consumerApiPath}/api/password/reset-password/request?username=${encodeURIComponent(
            value.email
          )}`,
          {}
        )
        .subscribe({
          next: () => {
            resolve(true);
          },
          error: (error) => {
            this.logger.log('Error requesting password recovery: ', error);
            resolve(false);
          }
        });
    });
  }

  /**
   * Request a password reset for a user.
   */
  async resetPassword(value: ResetPasswordModel): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.httpClient
        .post(`${environment.consumerApiPath}/api/password/reset-password/confirm`, {
          username: value.email,
          verificationCode: value.code,
          newPassword: value.password
        })
        .subscribe({
          next: () => {
            resolve(true);
          },
          error: (error) => {
            this.logger.log('Error setting new password: ', error);
            resolve(false);
          }
        });
    });
  }

  setAuthTokens(response: any): void {
    this.accessToken = response.accessToken;
    this.idToken = response.idToken;
    this.refreshToken = response.refreshToken;
  }

  getUserName(): string {
    const broker: BrokerModel = this.brokerData;
    if (broker) {
      return `${broker.firstName} ${broker.lastName}`;
    }
    return '-';
  }

  private async validateUserEmail(email: string): Promise<boolean> {
    return new Promise<boolean>((resolve: BooleanMessage, reject: ErrorResponseMessage): void => {
      this.httpClient
        .post<UserEmailValidationModel>(`${environment.consumerApiPath}/user/validateUserEmail`, {
          emailAddress: email
        })
        .subscribe(
          (response: UserEmailValidationModel) => resolve(response.userEmailExists),
          (error: HttpErrorResponse) => reject(error)
        );
    });
  }

  private validateUserAccount(): Promise<void> {
    return this.userService
      .validateAccount()
      .then((validation: UserValidationModel) => {
        this.logger.log('User account validated successfully.');

        this.canSubmitConsumerFlow =
          validation.isAdvicePlus || (validation.canSubmitConsumerFlow && validation.isActive);
        this.canRequestAdminAccess = validation.isAdmin && validation.isActive;
        this.canRequestAnyAccountAccess = validation.canViewAllBrokerAccountsApplications && validation.isActive;
        this.brokerData = validation.userInfo;
        this.isUserActive = validation.isActive;
        this.isAdvicePlus = validation.isAdvicePlus;
        this.isWhatsNewAllowed = validation.isWhatsNewAllowed;
        this.isSelinaEmployee = validation.isSelinaEmployee;
        this.isWhatsNewPopupAllowedHomeTrack = validation.isWhatsNewPopupAllowedHomeTrack;
      })
      .catch((error: HttpErrorResponse) => {
        this.logger.error('User account validation has failed.', error);
        this.canSubmitConsumerFlow = false;
        this.canRequestAdminAccess = false;
        this.canRequestAnyAccountAccess = false;
      });
  }
}
